import React, { useState, useRef, useMemo } from 'react'
import { debounce, Button, TextField, Box } from '@mui/material'
import { getAPIRequest } from 'infrastructure/api'
import '../../../styles/pages.css'
import { List } from 'infrastructure/api'
import { fetchSchools } from 'shared/api/list'
import { Table } from 'shared/ui/Table/Table'
import { TablePagination } from 'shared/ui/Table/TablePagination'

export default function SearchSchools({
  submitFn,
  disableFn,
  clearAfterSubmit,
}: {
  submitFn: (schoolId: number) => Promise<unknown>
  disableFn?: (districtSchool: List.School) => boolean
  clearAfterSubmit?: boolean
}) {
  const [schoolIDField, setSchoolIDField] = useState('')
  const [schoolNameField, setSchoolNameField] = useState('')
  const [totalSchools, setTotalSchools] = useState(0)
  const [page, setPage] = useState(0)
  const [error, setError] = useState('')
  const [schools, setSchools] = useState<List.School[]>([])

  const searchByName = useRef(
    debounce(async (schoolName: string, pageNumber: number) => {
      if (schoolName.length === 0) {
        return
      }
      setError('')
      try {
        const { data, total_count: total } = await fetchSchools({
          perPage: 5,
          currentPage: pageNumber + 1,
          filters: { schoolSearch: schoolName },
          sort: { field: 'name', order: 'asc' },
        })

        setSchools(data)
        if (data.length === 0) {
          setError('No schools with this parameters.')
        }
        setTotalSchools(total)
      } catch (e: any) {
        // error getting schools
        setError('Error getting schools, please try again.')
      }
    }, 800),
  )

  const searchById = useRef(
    debounce(async (schoolID: string) => {
      if (schoolID.length === 0) {
        return
      }
      setError('')
      if (schoolID) {
        try {
          const data = await getAPIRequest<List.School>(
            `/api/school/${schoolID}`,
          )
          setSchools([data])
        } catch (e: any) {
          // error getting schools
          setError('Error getting schools, please try again.')
        }
      }
    }, 800),
  )

  const onChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError('')
    setSchoolIDField('')
    setSchoolNameField(e.target.value)
    searchByName.current(e.target.value, 0)
    setPage(0)
  }
  const onChangeID = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError('')
    setSchoolNameField('')
    setSchoolIDField(e.target.value)
    searchById.current(e.target.value)
  }

  const clearFields = () => {
    setSchoolIDField('')
    setSchoolNameField('')
    setSchools([])
    setPage(0)
    setTotalSchools(0)
  }

  async function onSubmit(schoolID: number) {
    setError('')
    try {
      await submitFn(schoolID)
      // since submit is for both id and name search
      // only search by name if school name is present
      if (clearAfterSubmit) {
        clearFields()
      } else if (schoolNameField) {
        searchByName.current(schoolNameField, page)
      }
    } catch (e: any) {
      // error creating new school
      setError('Error adding school, please try again.')
    }
  }

  const handleChangePage = (newPage: number) => {
    setPage(newPage)
    searchByName.current(schoolNameField, newPage)
  }

  const schoolCols = useSchoolColumns(onSubmit, disableFn)

  return (
    <Box className="accordion-content">
      <Box className="accordion-element">
        <Box className="accordion-inputs-wrapper">
          <Box className="accordion-two-inputs">
            <TextField
              className="accordion-two-inputs school-name-search"
              aria-describedby="school-name-input"
              onChange={onChangeName}
              value={schoolNameField}
              label="Search by School Name"
            />
          </Box>
          <Box className="accordion-two-inputs">
            <TextField
              className="accordion-two-inputs school-id-search"
              aria-describedby="school-id-input"
              onChange={onChangeID}
              value={schoolIDField}
              label="Search by School ID"
            />
          </Box>
        </Box>
      </Box>
      {!!schools.length && (
        <Box className="accordion-element school-search-list">
          <Table
            columns={schoolCols}
            items={schools}
            overrideHeight={5 * 50 + 37 + 48}
            footer={
              <TablePagination
                total={totalSchools}
                page={page}
                rowsPerPage={5}
                rowsPerPageOptions={[5]}
                onPageChange={handleChangePage}
              />
            }
          />
        </Box>
      )}

      <Box className="accordion-element">
        {error && <p className="error">{error}</p>}
      </Box>
    </Box>
  )
}

function useSchoolColumns(
  onSubmit: (schoolID: number) => unknown,
  disableFn?: (districtSchool: List.School) => boolean,
) {
  return useMemo(
    () => [
      {
        title: <strong>School Name</strong>,
        width: 250,
        rowContent({ name }: List.School) {
          return name
        },
      },
      {
        title: <strong>School Id</strong>,
        width: 100,
        rowContent({ id }: List.School) {
          return id
        },
      },
      {
        title: <strong>District</strong>,
        width: 250,
        rowContent({ district }: List.School) {
          return district?.name
        },
      },
      {
        title: '',
        width: 200,
        rowContent(school: List.School) {
          return (
            <Button
              data-testid="submit"
              disabled={disableFn && disableFn(school)}
              onClick={() => {
                onSubmit(school.id)
              }}
              variant="outlined"
            >
              Add this school
            </Button>
          )
        },
      },
    ],
    [],
  )
}
